import { Map } from 'immutable';

import { getTimeRangeStart, getToday, patchTimeRanges } from './time';
import { getDateKey, getDaysDifference } from '../time';
import { shapeId } from '../listings/listings';
import { getSize } from '../iterable/getSize';
import { get } from '../iterable/get';

import { EMPTY, SEPARATOR } from '../../constants';
import { FIELD_NAME } from '../../constants/schedule.updates';

export function shapeScheduledUpdatesForApp({ response, state }) {
  const channel = state.get('channel');
  const scheduledUpdates = get('scheduled_updates')(response);

  if (getSize(scheduledUpdates) === 0) {
    const loaded = state.getIn(['updates', 'loaded'], 0);
    return state
      .setIn(['updates', 'loaded'], loaded)
      .setIn(['updates', 'total'], loaded);
  }

  const today = getToday();

  function reduceScheduledUpdates(result, scheduledUpdate) {
    const {
      changed_fields: changedFields,
      created_at: createdAt,
      id,
      listings,
      scheduled_at: scheduledAt,
    } = scheduledUpdate;

    const updateId = shapeId(id);
    const dateKey = getDateKey(scheduledAt);
    const { days } = getDaysDifference(today, new Date(scheduledAt), 0);
    const updateRange = getTimeRangeStart(today, days);
    const updateRangeKey = getDateKey(updateRange);

    function updateDates(dates = EMPTY.MAP) {
      if (dates.hasIn(['ranges', dateKey])) {
        return dates;
      }

      function addDateKey(keys = EMPTY.LIST) {
        return keys.push(dateKey);
      }

      function addRange(ranges = EMPTY.MAP) {
        return ranges.set(dateKey, updateRangeKey);
      }

      function addTimestamp(timestams = EMPTY.MAP) {
        return timestams.set(dateKey, scheduledAt);
      }

      return dates
        .update('keys', addDateKey)
        .update('ranges', addRange)
        .update('timestamps', addTimestamp);
    }

    function updateRanges(ranges = EMPTY.MAP) {
      if (ranges.hasIn(['timestamps', updateRangeKey])) {
        return ranges;
      }

      function addRangeKey(keys = EMPTY.LIST) {
        return keys.push(updateRangeKey);
      }

      function addTimestamp(timestamps = EMPTY.MAP) {
        return timestamps.set(updateRangeKey, updateRange);
      }

      return ranges
        .update('keys', addRangeKey)
        .update('timestamps', addTimestamp);
    }

    function updateUpdates(updates = EMPTY.MAP) {
      function reduceFields(reduced, field) {
        const name = FIELD_NAME[channel][field];

        return name && !reduced.has(name)
          ? reduced.add(name)
          : reduced;
      }

      function reduceListings(reduced, listing) {
        const { id: listingId, status, thumbnail, title } = listing;
        return reduced.push(Map({ id: shapeId(listingId), status, thumbnail, title }));
      }

      const update = Map({
        date: dateKey,
        createdAt,
        products: listings.reduce(reduceListings, EMPTY.LIST),
        scheduledAt,
        fields: changedFields
          .reduce(reduceFields, EMPTY.ORDERED_SET)
          .join(SEPARATOR.COMMA_SPACE),
      });

      function updateLoaded(loaded = 0) {
        return loaded + 1;
      }

      function addUpdateId(updatesByDate = EMPTY.LIST) {
        return updatesByDate.push(updateId);
      }

      return updates
        .setIn(['byId', updateId], update)
        .update('loaded', updateLoaded)
        .updateIn(['byDate', dateKey], addUpdateId);
    }

    return result
      .update('dates', updateDates)
      .update('ranges', updateRanges)
      .update('updates', updateUpdates);
  }

  return patchTimeRanges(scheduledUpdates.reduce(reduceScheduledUpdates, state));
}
